import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FileDown, Loader2, Save, Upload } from "lucide-react";

import config from "@/config";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  joinVobMarkdown,
  splitVobMarkdown,
  VobMarkdownContent,
} from "@/components/vob-markdown-content";
import { cn } from "@/utils";

interface VobMarkdownResponse {
  success: boolean;
  case_id: string;
  filename: string;
  content: string;
}

type EditorPanel = "yaml" | "review" | "pdf";

export interface VobBatchTarget {
  patient_id: string;
  j_code: string;
  date: string;
  case_ids: string[];
}

async function readErrorDetail(res: Response): Promise<string> {
  let detail = `Request failed (${res.status})`;
  try {
    const data = (await res.json()) as { detail?: unknown; message?: unknown };
    if (typeof data.detail === "string" && data.detail.trim()) {
      return data.detail;
    }
    if (typeof data.message === "string" && data.message.trim()) {
      return data.message;
    }
  } catch {
    // ignore non-JSON body
  }
  return detail;
}

function toYamlEditorValue(frontmatter: string): string {
  return frontmatter.trim() ? `---\n${frontmatter.trim()}\n---` : "---\n---";
}

function parseYamlEditorValue(value: string): string {
  const trimmed = value.trimStart();
  if (!trimmed.startsWith("---")) return trimmed;

  const end = trimmed.indexOf("\n---", 3);
  if (end === -1) return trimmed.slice(3).trim();
  return trimmed.slice(4, end);
}

export function VobMarkdownEditor({
  caseId,
  className,
  batch,
  onUploaded,
}: {
  caseId: string | null;
  className?: string;
  batch?: VobBatchTarget | null;
  onUploaded?: () => void;
}) {
  const [savedContent, setSavedContent] = useState("");
  const [draftContent, setDraftContent] = useState("");
  const [filename, setFilename] = useState<string | null>(null);
  const [panel, setPanel] = useState<EditorPanel>("yaml");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [pdfRefreshKey, setPdfRefreshKey] = useState(0);
  const [uploading, setUploading] = useState(false);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const { frontmatter, body } = useMemo(
    () => splitVobMarkdown(draftContent),
    [draftContent],
  );

  const yamlEditorValue = useMemo(
    () => toYamlEditorValue(frontmatter),
    [frontmatter],
  );

  const isDirty = useMemo(
    () => draftContent !== savedContent,
    [draftContent, savedContent],
  );

  const pdfPreviewUrl = useMemo(() => {
    if (!caseId) return null;
    const params = new URLSearchParams({ v: String(pdfRefreshKey) });
    return `${config.API_BASE_URL}/api/dashboard/cases/${encodeURIComponent(caseId)}/vob-pdf?${params.toString()}`;
  }, [caseId, pdfRefreshKey]);

  const loadMarkdown = useCallback(async (targetCaseId: string) => {
    setLoading(true);
    setError(null);
    setStatusMessage(null);

    try {
      const res = await fetch(
        `${config.API_BASE_URL}/api/dashboard/cases/${encodeURIComponent(targetCaseId)}/vob-md`,
      );
      if (!res.ok) {
        throw new Error(await readErrorDetail(res));
      }

      const data = (await res.json()) as VobMarkdownResponse;
      setSavedContent(data.content);
      setDraftContent(data.content);
      setFilename(data.filename);
    } catch (err) {
      setSavedContent("");
      setDraftContent("");
      setFilename(null);
      setError(err instanceof Error ? err.message : "Failed to load VOB markdown");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!caseId) {
      setSavedContent("");
      setDraftContent("");
      setFilename(null);
      setError(null);
      setStatusMessage(null);
      setLoading(false);
      return;
    }

    void loadMarkdown(caseId);
  }, [caseId, loadMarkdown]);

  const saveMarkdown = useCallback(async () => {
    if (!caseId || !draftContent.trim()) return;

    setSaving(true);
    setError(null);
    setStatusMessage(null);

    try {
      const res = await fetch(
        `${config.API_BASE_URL}/api/dashboard/cases/${encodeURIComponent(caseId)}/vob-md`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ content: draftContent }),
        },
      );
      if (!res.ok) {
        throw new Error(await readErrorDetail(res));
      }

      setSavedContent(draftContent);
      setStatusMessage("Markdown saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save VOB markdown");
    } finally {
      setSaving(false);
    }
  }, [caseId, draftContent]);

  const rebuildDocx = useCallback(async () => {
    if (!caseId || !draftContent.trim()) return;

    setRebuilding(true);
    setError(null);
    setStatusMessage(null);

    try {
      const res = await fetch(
        `${config.API_BASE_URL}/api/dashboard/cases/${encodeURIComponent(caseId)}/vob-rebuild-docx`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ content: draftContent }),
        },
      );
      if (!res.ok) {
        throw new Error(await readErrorDetail(res));
      }

      setSavedContent(draftContent);
      setPdfRefreshKey((value) => value + 1);
      setStatusMessage("DOCX and PDF rebuilt from the YAML frontmatter.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to rebuild VOB docx");
    } finally {
      setRebuilding(false);
    }
  }, [caseId, draftContent]);

  const uploadVobFolder = useCallback(
    async (fileList: FileList) => {
      if (!batch || fileList.length === 0) return;

      setUploading(true);
      setError(null);
      setStatusMessage(null);

      try {
        const formData = new FormData();
        formData.append("patient_id", batch.patient_id);
        formData.append("j_code", batch.j_code);
        formData.append("date", batch.date);
        formData.append("case_ids", batch.case_ids.join(","));

        for (const file of Array.from(fileList)) {
          const relativePath = file.webkitRelativePath || file.name;
          formData.append("files", file, relativePath);
        }

        const res = await fetch(`${config.API_BASE_URL}/api/dashboard/vob/upload`, {
          method: "POST",
          body: formData,
        });
        if (!res.ok) {
          throw new Error(await readErrorDetail(res));
        }

        setStatusMessage("VOB folder uploaded and linked to cases.");
        setPdfRefreshKey((value) => value + 1);
        onUploaded?.();

        const reloadCaseId = caseId ?? batch.case_ids[0] ?? null;
        if (reloadCaseId) {
          await loadMarkdown(reloadCaseId);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to upload VOB folder");
      } finally {
        setUploading(false);
        if (folderInputRef.current) {
          folderInputRef.current.value = "";
        }
      }
    },
    [batch, caseId, loadMarkdown, onUploaded],
  );

  if (!caseId && !batch) return null;

  return (
    <section
      className={cn("flex min-h-0 flex-col rounded-lg border bg-card", className)}
    >
      <div className="shrink-0 space-y-2 border-b px-3 py-2">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <p className="text-sm font-medium">Generated VOB</p>
            {filename ? (
              <p className="text-xs font-mono text-muted-foreground">{filename}</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Shared across all cases in this batch
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1">
            <Button
              type="button"
              size="sm"
              variant={panel === "yaml" ? "default" : "outline"}
              className="h-7 px-2 text-xs"
              onClick={() => setPanel("yaml")}
            >
              YAML
            </Button>
            <Button
              type="button"
              size="sm"
              variant={panel === "review" ? "default" : "outline"}
              className="h-7 px-2 text-xs"
              onClick={() => setPanel("review")}
            >
              Review
            </Button>
            <Button
              type="button"
              size="sm"
              variant={panel === "pdf" ? "default" : "outline"}
              className="h-7 px-2 text-xs"
              onClick={() => setPanel("pdf")}
            >
              PDF
            </Button>
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          {panel === "yaml"
            ? "Edit the YAML frontmatter. These values drive the exported DOCX and PDF."
            : panel === "review"
              ? "Read-only preview of the generated review markdown."
              : "Preview of the exported DOCX as PDF. Rebuild DOCX to refresh."}
          {isDirty ? " Unsaved changes." : null}
        </p>

        <div className="flex flex-wrap items-center gap-2">
          {batch ? (
            <>
              <input
                ref={folderInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(event) => {
                  const selected = event.target.files;
                  if (selected && selected.length > 0) {
                    void uploadVobFolder(selected);
                  }
                }}
                {...({ webkitdirectory: "", directory: "" } as Record<string, string>)}
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 gap-1 text-xs"
                disabled={uploading || loading}
                onClick={() => folderInputRef.current?.click()}
              >
                {uploading ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Upload className="h-3 w-3" />
                )}
                Upload VOB
              </Button>
            </>
          ) : null}
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 gap-1 text-xs"
            disabled={loading || saving || !draftContent.trim() || !isDirty}
            onClick={() => void saveMarkdown()}
          >
            {saving ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Save className="h-3 w-3" />
            )}
            Save markdown
          </Button>
          <Button
            type="button"
            size="sm"
            className="h-7 gap-1 text-xs"
            disabled={loading || rebuilding || !draftContent.trim()}
            onClick={() => void rebuildDocx()}
          >
            {rebuilding ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <FileDown className="h-3 w-3" />
            )}
            Rebuild DOCX
          </Button>
        </div>

        {statusMessage ? (
          <p className="text-xs text-emerald-700">{statusMessage}</p>
        ) : null}
        {error ? <p className="text-xs text-destructive">{error}</p> : null}
      </div>

      {panel === "pdf" ? (
        <div className="min-h-0 flex-1 p-3">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading VOB preview…
            </div>
          ) : pdfPreviewUrl ? (
            <iframe
              key={pdfPreviewUrl}
              src={pdfPreviewUrl}
              title="VOB PDF preview"
              className="h-full min-h-[28rem] w-full rounded-md border bg-background"
            />
          ) : (
            <p className="text-sm italic text-muted-foreground">No PDF preview available.</p>
          )}
        </div>
      ) : (
        <ScrollArea className="min-h-0 flex-1">
          <div className="p-3">
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading VOB markdown…
              </div>
            ) : panel === "yaml" ? (
              <textarea
                value={yamlEditorValue}
                onChange={(event) => {
                  const nextFrontmatter = parseYamlEditorValue(event.target.value);
                  setDraftContent(joinVobMarkdown(nextFrontmatter, body));
                }}
                spellCheck={false}
                className={cn(
                  "min-h-[28rem] w-full resize-y rounded-md border bg-background p-3",
                  "font-mono text-xs leading-relaxed text-foreground",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                )}
                aria-label="VOB YAML frontmatter editor"
              />
            ) : body ? (
              <VobMarkdownContent content={body} />
            ) : (
              <p className="text-sm italic text-muted-foreground">No review markdown loaded.</p>
            )}
          </div>
        </ScrollArea>
      )}
    </section>
  );
}
